/*
 * Fullscreen for the embedded editor.
 *
 * While embedded, the editor fills an iframe rather than the window, so the host
 * cannot simply grow it: the Fullscreen API is the only way to hand the user a
 * bigger canvas. It works from inside an iframe as long as the host allows it
 * (`allow="fullscreen"`), and it needs no cooperation from the host otherwise,
 * which is why this is not part of the postMessage bridge.
 *
 * The prefixed names are kept because the editor still ships to browsers where
 * only those exist.
 */

const getDocument = () => (typeof document === 'undefined' ? null : document);

const getFullscreenElement = () => {
    const doc = getDocument();
    if (!doc) return null;
    return doc.fullscreenElement || doc.webkitFullscreenElement || null;
};

const isFullscreen = () => Boolean(getFullscreenElement());

// Both calls are best effort: a refusal is the browser's to make, and the caller
// only needs to know that it was asked.
const settle = result => {
    if (result && typeof result.catch === 'function') result.catch(() => {});
};

const enterFullscreen = () => {
    const doc = getDocument();
    if (!doc || !doc.documentElement) return false;
    const element = doc.documentElement;
    const request = element.requestFullscreen || element.webkitRequestFullscreen;
    if (typeof request !== 'function') return false;
    settle(request.call(element));
    return true;
};

const exitFullscreen = () => {
    const doc = getDocument();
    if (!doc) return false;
    const leave = doc.exitFullscreen || doc.webkitExitFullscreen;
    if (typeof leave !== 'function') return false;
    settle(leave.call(doc));
    return true;
};

// Returns the state the caller should expect next, so a caller that tracks its
// own label can flip it without waiting for the fullscreenchange event.
const toggleFullscreen = () => {
    if (isFullscreen()) {
        exitFullscreen();
        return false;
    }
    enterFullscreen();
    return true;
};

export {
    enterFullscreen,
    exitFullscreen,
    getFullscreenElement,
    isFullscreen,
    toggleFullscreen
};
